import { Box, Paper, Stack, Typography } from '@mui/material';
import { Scrollbar } from 'app/components';
import { useDebounce, useLocalStorage } from 'app/services';
import { colorDun1, colorDun2, colorDun3, colorDun4, colorDun5, colorDun6, colorDunCover, colorDunCover2 } from 'app/theme';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Area, Brush, CartesianGrid, ComposedChart, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
function buildInitialBrushTicks(selectedDate) {
    const brushDate = moment(selectedDate[0]);
    const length = moment(selectedDate[1]).diff(selectedDate[0], 'hours') + 1;
    return Array.from({ length }, (_, key) => brushDate
        .startOf('hour')
        .add(key ? 1 : 0, 'hours')
        .toDate()
        .getTime());
}
const MapTransportSynchronizedChart = ({ isFuelTank }) => {
    const scrollRef = useRef(null);
    const conditionItems = useSelector((state) => { var _a, _b; return (_b = (_a = state.map.transportSelectedItem) === null || _a === void 0 ? void 0 : _a.conditions) !== null && _b !== void 0 ? _b : []; });
    const selectedDate = useSelector((state) => state.map.transportSelectedDate);
    const [brush] = useState(() => buildInitialBrushTicks(selectedDate));
    const [ticks, setTicks] = useState(brush);
    const [disabledLegend, setDisabledLegend] = useLocalStorage('transportSynchronizedChartDisabledLegend', []);
    const [scrollTop, setScrollTop] = useLocalStorage('transportSynchronizedChartScroll', 0);
    const data = useMemo(() => {
        return conditionItems
            .filter((condition) => condition.type === 'message')
            .reduce((accumulator, currentValue) => {
            var _a;
            const parkingTime = (_a = currentValue.data.parking_time) !== null && _a !== void 0 ? _a : 0;
            const currentElement = Object.assign(Object.assign({}, currentValue.data), { datetime: moment(currentValue.datetime).toDate().getTime(), parking_time: Math.round(parkingTime / 60), parking_time_double: parkingTime !== 0 ? (isFuelTank ? 3100 : 1100) : 0 });
            return [...accumulator, currentElement];
        }, []);
    }, [conditionItems]);
    const domain = useMemo(() => {
        if (ticks[ticks.length - 1] === moment(selectedDate[1]).startOf('hour').toDate().getTime()) {
            return [ticks[0], moment(selectedDate[1]).toDate().getTime()];
        }
        return [ticks[0], ticks[ticks.length - 1]];
    }, [conditionItems, ticks]);
    const handleLegendClick = (dataKey) => {
        if (disabledLegend.includes(dataKey)) {
            setDisabledLegend(disabledLegend.filter((legend) => legend !== dataKey));
        }
        else {
            setDisabledLegend(disabledLegend.concat(dataKey));
        }
    };
    const CustomTooltip = ({ active, payload, label, options = [] }) => {
        if (active && payload) {
            return (React.createElement(Stack, { direction: 'column', sx: (theme) => ({
                    fontSize: '12px',
                    bgcolor: theme.palette.grey.dark,
                    overflow: 'hidden',
                    padding: '3px 8px',
                    borderRadius: theme.shape.borderRadius,
                    color: theme.palette.common.white,
                    label: {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '12px',
                        fontWeight: '500',
                    },
                    div: {
                        display: 'flex',
                        alignItems: 'center',
                        'span:first-of-type': {
                            width: '8px',
                            height: '8px',
                            marginRight: '8px',
                            borderRadius: '500px',
                        },
                        'span:nth-of-type(2)': {
                            marginRight: '30px',
                        },
                        'span:last-of-type': {
                            display: 'flex',
                            flex: '1',
                            justifyContent: 'flex-end',
                        },
                    },
                }) },
                React.createElement(Typography, { component: 'label', variant: 'body1' }, moment(label).format('DD.MM.YYYY HH:mm:ss')),
                payload.map(({ dataKey, value, color }, key) => {
                    var _a;
                    const option = options.find((item) => item.dataKey === dataKey);
                    if (!option) {
                        return React.createElement(React.Fragment, { key: key });
                    }
                    return (React.createElement(Box, { key: key, component: 'div' },
                        React.createElement("span", { style: { backgroundColor: color } }),
                        React.createElement(Typography, { component: 'span', variant: 'body1' }, option.label),
                        React.createElement(Typography, { component: 'span', variant: 'body1' }, `${value} ${(_a = option.unit) !== null && _a !== void 0 ? _a : ''}`)));
                })));
        }
        return null;
    };
    const CustomLegend = ({ payload }) => {
        if (payload) {
            return (React.createElement(Stack, { direction: 'row', justifyContent: 'center', sx: {
                    fontSize: '12px',
                    marginLeft: '16px',
                    div: {
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                            cursor: 'pointer',
                        },
                        'span:first-of-type': {
                            width: '8px',
                            height: '8px',
                            marginRight: '6px',
                            borderRadius: '500px',
                        },
                    },
                    'div + div': {
                        marginLeft: '18px',
                    },
                } }, payload.map(({ dataKey, label, color }, key) => (React.createElement(Box, { key: key, component: 'div', onClick: () => handleLegendClick(dataKey), sx: Object.assign({}, (disabledLegend.indexOf(dataKey) !== -1 && {
                    opacity: '0.35',
                })) },
                React.createElement("span", { style: { backgroundColor: color } }),
                React.createElement(Typography, { component: 'span', variant: 'body1' }, label))))));
        }
        return null;
    };
    const handleBrushChange = (e) => {
        var _a;
        setTicks(brush.slice(e.startIndex, ((_a = e.endIndex) !== null && _a !== void 0 ? _a : 0) + 1));
    };
    const handleScrollEvent = useDebounce((e) => {
        setScrollTop(e.target.scrollTop);
    }, 300);
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.getScrollElement().scrollTop = scrollTop;
        }
    }, []);
    const speedLegendPayload = [
        {
            dataKey: 'can_speed',
            label: 'Скорость',
            color: '#4caf50',
        },
        {
            dataKey: 'voltage',
            label: 'Напряжение',
            color: '#d82424',
        },
        {
            dataKey: 'fuel_level',
            label: 'Уровень топлива',
            color: '#f4aa28',
        },
    ];
    const dutLegendPayload = [
        {
            dataKey: 'dut1',
            label: 'ДУТ1',
            color: '#d737db',
        },
        {
            dataKey: 'dut2',
            label: 'ДУТ2',
            color: '#83b7a8',
        },
        {
            dataKey: 'dut3',
            label: 'ДУТ3',
            color: '#248de1',
        },
        {
            dataKey: 'dut4',
            label: 'ДУТ4',
            color: '#6d28d8',
        },
        {
            dataKey: 'dut5',
            label: 'ДУТ5',
            color: '#f4db03',
        },
        {
            dataKey: 'dut6',
            label: 'ДУТ6',
            color: '#ea4d1c',
        },
        {
            dataKey: 'parking_time',
            label: 'Время парковки',
            color: '#dbcaf6',
        },
    ];
    const legendPayload = [
        {
            dataKey: 'dun1',
            label: 'ДУН1',
            color: colorDun1,
        },
        {
            dataKey: 'dun2',
            label: 'ДУН2',
            color: colorDun2,
        },
        {
            dataKey: 'dun3',
            label: 'ДУН3',
            color: colorDun3,
        },
        {
            dataKey: 'dun4',
            label: 'ДУН4',
            color: colorDun4,
        },
        {
            dataKey: 'dun5',
            label: 'ДУН5',
            color: colorDun5,
        },
        {
            dataKey: 'dun6',
            label: 'ДУН6',
            color: colorDun6,
        },
        {
            dataKey: 'dun_cover',
            label: 'Крышка',
            color: colorDunCover,
        },
        {
            dataKey: 'dun_cover_2',
            label: 'Крышка 17',
            color: colorDunCover2,
        },
    ];
    const tooltipOptions = [
        {
            dataKey: 'dun1',
            label: 'ДУН1',
            unit: config.conditions.dun.unit,
        },
        {
            dataKey: 'dun2',
            label: 'ДУН2',
            unit: config.conditions.dun.unit,
        },
        {
            dataKey: 'dun3',
            label: 'ДУН3',
            unit: config.conditions.dun.unit,
        },
        {
            dataKey: 'dun4',
            label: 'ДУН4',
            unit: config.conditions.dun.unit,
        },
        {
            dataKey: 'dun5',
            label: 'ДУН5',
            unit: config.conditions.dun.unit,
        },
        {
            dataKey: 'dun6',
            label: 'ДУН6',
            unit: config.conditions.dun.unit,
        },
        {
            dataKey: 'dun_cover',
            label: 'Крышка',
            unit: config.conditions.dun.unit,
        },
        {
            dataKey: 'dun_cover_2',
            label: 'Крышка 17',
            unit: config.conditions.dun.unit,
        },
    ];
    return (React.createElement(Scrollbar, { ref: scrollRef, onScroll: handleScrollEvent },
        React.createElement(Box, { component: 'div', sx: (theme) => ({
                display: 'grid',
                gap: 1,
                padding: theme.spacing(2),
                '.MuiPaper-root': {
                    position: 'relative',
                    borderStyle: 'solid',
                    padding: theme.spacing(2),
                    borderRadius: theme.shape.borderRadius,
                    borderColor: theme.palette.divider,
                },
                '.recharts-responsive-container': {
                    text: {
                        fontFamily: '"Roboto",sans-serif',
                        fontSize: '12px',
                    },
                },
                '.recharts-brush-texts': {
                    display: 'none',
                },
            }) },
            React.createElement(Paper, { variant: 'outlined' },
                React.createElement(ResponsiveContainer, { width: '100%', height: 230 },
                    React.createElement(LineChart, { width: 500, height: 200, data: data, syncId: 'mapSpeedChart', margin: {
                            top: 0,
                            right: 12,
                            left: -16,
                            bottom: 0,
                        } },
                        React.createElement(CartesianGrid, { strokeDasharray: '3 3' }),
                        React.createElement(XAxis, { dataKey: 'datetime', scale: 'time', type: 'number', allowDataOverflow: true, domain: domain, ticks: ticks, tickFormatter: (datetime) => moment(datetime).format('HH:mm') }),
                        React.createElement(YAxis, { type: 'number', allowDataOverflow: true, domain: [0, 100] }),
                        React.createElement(Tooltip, Object.assign({ animationDuration: 0, content: React.createElement(CustomTooltip, null) }, {
                            options: [
                                {
                                    dataKey: 'can_speed',
                                    label: 'Скорость',
                                    unit: config.conditions.can_speed.unit,
                                },
                                {
                                    dataKey: 'voltage',
                                    label: 'Напряжение',
                                    unit: config.conditions.voltage.unit,
                                },
                                {
                                    dataKey: 'fuel_level',
                                    label: 'Уровень топлива',
                                    unit: '%',
                                },
                            ],
                        })),
                        React.createElement(Legend, { verticalAlign: 'top', height: 30, content: React.createElement(CustomLegend, null), payload: speedLegendPayload }),
                        disabledLegend.indexOf('can_speed') === -1 && (React.createElement(Line, { dataKey: 'can_speed', type: 'monotone', stroke: '#4caf50', dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('voltage') === -1 && (React.createElement(Line, { dataKey: 'voltage', type: 'monotone', stroke: '#d82424', dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('fuel_level') === -1 && (React.createElement(Line, { dataKey: 'fuel_level', type: 'monotone', stroke: '#f4aa28', dot: false, animationDuration: 0 })))),
                React.createElement(ResponsiveContainer, { width: '100%', height: 30 },
                    React.createElement(LineChart, Object.assign({ width: 0, height: 60, data: brush }, { domain: [brush[0], brush[brush.length - 1]] }, { margin: {
                            top: 0,
                            right: 12,
                            left: -16,
                            bottom: 0,
                        } }),
                        React.createElement(YAxis, null),
                        React.createElement(Brush, { height: 30, stroke: '#8884d8', onChange: handleBrushChange })))),
            React.createElement(Paper, { variant: 'outlined' },
                React.createElement(ResponsiveContainer, { width: '100%', height: 230 },
                    React.createElement(ComposedChart, { width: 500, height: 200, data: data, syncId: 'mapDutChart', margin: {
                            top: 0,
                            right: 12,
                            left: -16,
                            bottom: 0,
                        } },
                        React.createElement(CartesianGrid, { strokeDasharray: '3 3' }),
                        React.createElement(XAxis, { dataKey: 'datetime', scale: 'time', type: 'number', allowDataOverflow: true, domain: domain, ticks: ticks, tickFormatter: (datetime) => moment(datetime).format('HH:mm') }),
                        React.createElement(YAxis, { type: 'number', allowDataOverflow: true, domain: isFuelTank ? [0, 3100] : [0, 1100] }),
                        React.createElement(Tooltip, Object.assign({ animationDuration: 0, content: React.createElement(CustomTooltip, null) }, {
                            options: [
                                {
                                    dataKey: 'dut1',
                                    label: 'ДУТ1',
                                    unit: config.conditions.dut.unit,
                                },
                                {
                                    dataKey: 'dut2',
                                    label: 'ДУТ2',
                                    unit: config.conditions.dut.unit,
                                },
                                {
                                    dataKey: 'dut3',
                                    label: 'ДУТ3',
                                    unit: config.conditions.dut.unit,
                                },
                                {
                                    dataKey: 'dut4',
                                    label: 'ДУТ4',
                                    unit: config.conditions.dut.unit,
                                },
                                {
                                    dataKey: 'dut5',
                                    label: 'ДУТ5',
                                    unit: config.conditions.dut.unit,
                                },
                                {
                                    dataKey: 'dut6',
                                    label: 'ДУТ6',
                                    unit: config.conditions.dut.unit,
                                },
                            ],
                        })),
                        React.createElement(Legend, { verticalAlign: 'top', height: 30, content: React.createElement(CustomLegend, null), payload: dutLegendPayload }),
                        disabledLegend.indexOf('dut1') === -1 && (React.createElement(Line, { dataKey: 'dut1', type: 'monotone', stroke: '#d737db', dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dut2') === -1 && (React.createElement(Line, { dataKey: 'dut2', type: 'monotone', stroke: '#83b7a8', dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dut3') === -1 && (React.createElement(Line, { dataKey: 'dut3', type: 'monotone', stroke: '#248de1', dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dut4') === -1 && (React.createElement(Line, { dataKey: 'dut4', type: 'monotone', stroke: '#a6a7e1', dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dut5') === -1 && (React.createElement(Line, { dataKey: 'dut5', type: 'monotone', stroke: '#f4db03', dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dut6') === -1 && (React.createElement(Line, { dataKey: 'dut6', type: 'monotone', stroke: '#ea4d1c', dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('parking_time') === -1 && (React.createElement(Area, { dataKey: 'parking_time_double', type: 'monotone', stroke: 'none', fill: '#6d28d8', fillOpacity: 0.15, dot: false, animationDuration: 1 }))))),
            React.createElement(Paper, { variant: 'outlined' },
                React.createElement(ResponsiveContainer, { width: '100%', height: 230 },
                    React.createElement(LineChart, { width: 500, height: 200, data: data, syncId: 'mapDunChart', margin: {
                            top: 0,
                            right: 12,
                            left: -16,
                            bottom: 0,
                        } },
                        React.createElement(CartesianGrid, { strokeDasharray: '3 3' }),
                        React.createElement(XAxis, { dataKey: 'datetime', scale: 'time', type: 'number', allowDataOverflow: true, domain: domain, ticks: ticks, tickFormatter: (datetime) => moment(datetime).format('HH:mm') }),
                        React.createElement(YAxis, { type: 'number', allowDataOverflow: true, domain: [0, 180] }),
                        React.createElement(Tooltip, Object.assign({ animationDuration: 0, content: React.createElement(CustomTooltip, null) }, { options: tooltipOptions })),
                        React.createElement(Legend, { verticalAlign: 'top', height: 30, content: React.createElement(CustomLegend, null), payload: legendPayload }),
                        disabledLegend.indexOf('dun1') === -1 && (React.createElement(Line, { dataKey: 'dun1', type: 'monotone', stroke: colorDun1, dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dun2') === -1 && (React.createElement(Line, { dataKey: 'dun2', type: 'monotone', stroke: colorDun2, dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dun3') === -1 && (React.createElement(Line, { dataKey: 'dun3', type: 'monotone', stroke: colorDun3, dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dun4') === -1 && (React.createElement(Line, { dataKey: 'dun4', type: 'monotone', stroke: colorDun4, dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dun5') === -1 && (React.createElement(Line, { dataKey: 'dun5', type: 'monotone', stroke: colorDun5, dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dun6') === -1 && (React.createElement(Line, { dataKey: 'dun6', type: 'monotone', stroke: colorDun6, dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dun_cover') === -1 && (React.createElement(Line, { dataKey: 'dun_cover', type: 'monotone', stroke: colorDunCover, dot: false, animationDuration: 0 })),
                        disabledLegend.indexOf('dun_cover_2') === -1 && (React.createElement(Line, { dataKey: 'dun_cover_2', type: 'monotone', stroke: colorDunCover2, dot: false, animationDuration: 0 }))))))));
};
export default MapTransportSynchronizedChart;
